import clsx from "clsx";
import React from "react";
import { Box, BoxProps, ToggleButton, ToggleButtonGroup } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import LightModeIcon from "@mui/icons-material/LightMode";
import DarkModeIcon from "@mui/icons-material/DarkMode";
import SettingsBrightnessIcon from "@mui/icons-material/SettingsBrightness";

import { useThemeMode } from "../../hooks/useThemeMode";
import { ThemeMode } from "../../theme";
import { FieldsGroup } from "./FieldsGroup";

const useStyles = makeStyles((theme) => ({
  root: {},

  toggleGroup: {
    borderRadius: theme.spacing(0.5),
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
  },

  toggleButton: {
    flex: 1,
    padding: theme.spacing(1.5, 3),
    border: "none",
    borderRadius: 0,
    textTransform: "none",
    fontWeight: 500,
    fontSize: 14,
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.paper,

    "&.MuiToggleButtonGroup-grouped:not(:first-of-type)": {
      borderLeft: `1px solid ${theme.palette.divider}`,
      marginLeft: 0,
    },

    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,

      "&:hover": {
        backgroundColor: theme.palette.primary.dark,
      },
    },

    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },

    "& .MuiSvgIcon-root": {
      marginRight: theme.spacing(1),
    },
  },
}));

export interface ThemeModeSwitcherProps extends Omit<BoxProps, "onChange"> {
  label?: string;
  disabled?: boolean;
}

export function ThemeModeSwitcher(props: ThemeModeSwitcherProps) {
  const { className, label = "Appearance", disabled, ...other } = props;
  const s = useStyles();
  const { mode, setMode } = useThemeMode();

  const handleChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMode: ThemeMode | null,
  ) => {
    if (newMode !== null) {
      setMode(newMode);
    }
  };

  return (
    <FieldsGroup className={clsx(s.root, className)} label={label}>
      <ToggleButtonGroup
        className={s.toggleGroup}
        value={mode}
        exclusive
        onChange={handleChange}
        aria-label="theme mode"
        fullWidth
      >
        <ToggleButton
          className={s.toggleButton}
          value="light"
          aria-label="light mode"
          disabled={disabled}
        >
          <LightModeIcon />
          Light
        </ToggleButton>
        <ToggleButton
          className={s.toggleButton}
          value="dark"
          aria-label="dark mode"
          disabled={disabled}
        >
          <DarkModeIcon />
          Dark
        </ToggleButton>
        <ToggleButton
          className={s.toggleButton}
          value="system"
          aria-label="system mode"
          disabled={disabled}
        >
          <SettingsBrightnessIcon />
          System
        </ToggleButton>
      </ToggleButtonGroup>
    </FieldsGroup>
  );
}
